import React from "react";
import { GiftCardType } from "@/lib/supabase/types";
import { 
  Table, 
  TableBody, 
  TableCaption, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Gift, Plus, Download, Search } from "lucide-react";
import { format, parseISO, isAfter } from "date-fns";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";

interface GiftCardsTabProps {
  giftCards?: GiftCardType[];
  isLoading: boolean;
  error: unknown;
  onAddGiftCard: () => void;
  filteredGiftCards?: GiftCardType[];
  giftCardFilter?: string;
  setGiftCardFilter?: (filter: string) => void;
}

const GiftCardsTab: React.FC<GiftCardsTabProps> = ({
  giftCards,
  isLoading,
  error,
  onAddGiftCard,
  filteredGiftCards,
  giftCardFilter = "",
  setGiftCardFilter = () => {}
}) => {
  const displayGiftCards = filteredGiftCards || giftCards;
  
  const handleDownloadGiftCard = async (card: GiftCardType) => {
    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      
      if (!ctx) {
        toast.error("Your browser doesn't support canvas operations");
        return;
      }
      
      canvas.width = 800;
      canvas.height = 800;
      
      const templateImage = new Image();
      templateImage.crossOrigin = "anonymous";
      templateImage.src = "/lovable-uploads/46e2e57c-77a0-4c78-90c3-6090d1d66213.png";
      
      await new Promise((resolve, reject) => {
        templateImage.onload = resolve;
        templateImage.onerror = () => {
          reject(new Error("Failed to load template image"));
        };
      });
      
      ctx.drawImage(templateImage, 0, 0, canvas.width, canvas.height);
      
      ctx.fillStyle = "#000000";
      ctx.font = "24px monospace";
      ctx.textAlign = "left";
      ctx.fillText("Code", 60, 520);
      
      ctx.font = "bold 36px monospace";
      ctx.fillText(card.code, 60, 565);
      
      ctx.fillStyle = "#000000";
      ctx.font = "32px 'Comic Sans MS', 'Calibri', sans-serif";
      ctx.textAlign = "center";
      const recipientName = `${card.firstName} ${card.lastName}`;
      ctx.fillText(recipientName, canvas.width / 2, canvas.height - 50);
      
      const imageDataUrl = canvas.toDataURL("image/png");
      
      const downloadLink = document.createElement("a");
      downloadLink.href = imageDataUrl;
      downloadLink.download = `giftcard-${card.code}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      
      toast.success("Gift card downloaded successfully");
    } catch (error) {
      console.error("Error generating gift card:", error);
      toast.error("Failed to generate gift card image");
    }
  };

  if (isLoading) {
    return <div className="flex justify-center p-8">Loading gift cards...</div>;
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-500 p-4 rounded-md">
        Error loading gift cards: {String(error)}
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Gift Cards</h2>
        <Button onClick={onAddGiftCard} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Create Gift Card
        </Button>
      </div>
      
      <div className="relative w-full max-w-sm mb-6">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Search by recipient name"
          className="pl-8 w-full"
          value={giftCardFilter}
          onChange={(e) => setGiftCardFilter(e.target.value)}
        />
      </div>

      {displayGiftCards && displayGiftCards.length > 0 ? (
        <Table>
          <TableCaption>A list of all gift cards</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Recipient</TableHead>
              <TableHead>Classes</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayGiftCards.map((card) => {
              const isExpired = isAfter(
                new Date(),
                parseISO(card.expirationDate)
              );
              const isUsedUp = card.remainingClasses <= 0;
              const status = !card.isActive 
                ? "inactive"
                : isExpired 
                  ? "expired" 
                  : isUsedUp 
                    ? "used" 
                    : "active";

              return (
                <TableRow key={card.id}>
                  <TableCell className="font-mono font-medium">
                    {card.code}
                  </TableCell>
                  <TableCell>
                    {card.firstName} {card.lastName}
                  </TableCell>
                  <TableCell>
                    {card.remainingClasses}/{card.totalClasses}
                  </TableCell>
                  <TableCell>
                    {format(parseISO(card.createdAt), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell>
                    {format(parseISO(card.expirationDate), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell>
                    <Badge 
                      variant={
                        status === "active" 
                          ? "default" 
                          : status === "used" || status === "expired" 
                            ? "outline" 
                            : "destructive"
                      }
                    >
                      {status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleDownloadGiftCard(card)}
                      className="flex items-center gap-1"
                    >
                      <Download className="h-4 w-4" />
                      Download
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : (
        <div className="text-center py-8">
          {giftCardFilter ? (
            <>
              <Gift className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-medium">No matching gift cards found</h3>
              <p className="text-muted-foreground mt-2">
                Try a different search term
              </p>
            </>
          ) : (
            <>
              <Gift className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-medium">No gift cards found</h3>
              <p className="text-muted-foreground mt-2">
                Create your first gift card to get started
              </p>
              <Button onClick={onAddGiftCard} className="mt-4">
                Create Gift Card
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default GiftCardsTab;
